import Vue from 'vue'
import Vuex from 'vuex'

import router from '@/router'

import { login } from '@/api/auth'
import { getLoginUser, register, contact, confirmEmail, updateProfile, withdraw, confirmRegister, verifyToken, newPassword } from '@/api/user'
import { getToken, setToken } from '@/lib/functions'
import { updateBookmarksStatus } from '@/api/content'

Vue.use(Vuex)

const state: store.AppState = {
  title: process.env.VUE_APP_NAME || '',
  loading: false,
  token: getToken(),
  user: undefined,
  menu: [],
  message: {
    show: false,
    text: '',
    type: ''
  },
  contact: {
    isComplete: false
  },
  answers: [] as any
}

const actions = {
  // ローディング表示
  async loading({ commit }: any, loading: boolean = true) {
    commit('SET_LOADING', loading)
  },

  // メッセージ表示
  async showMessage({ commit }: any, message: string) {
    commit('SET_MESSAGE', { show: true, text: message, type: 'info' })
  },
  async showErrorMessage({ commit }: any, message: string) {
    commit('SET_MESSAGE', { show: true, text: message, type: 'warning' })
  },
  async clearMessage({ commit }: any) {
    commit('SET_MESSAGE', { show: false, text: '', type: '' })
  },

  // ログイン
  async login({ commit }: any, { email, password }: store.actions.LoginParam) {
    const res = await login(email, password)
    commit('SET_TOKEN', res.data.token)
    return res.data
  },

  // ログアウト
  logout({ commit }: any) {
    commit('SET_TOKEN', undefined)
    commit('SET_USER', undefined)
    router.push('/login')
  },

  // 画面タイトル設定
  async setTitle({ commit }: any, title: string) {
    commit('SET_TITLE', title)
  },

  // メニュー項目設定
  async setMenu({ commit }: any, links: store.MenuLink[]) {
    commit('SET_MENU', links)
  },
  async setMenuDefault({ commit }: any) {
    commit('SET_MENU', [
      // tslint:disable-next-line: no-empty tslint:disable-next-line: handle-callback-err
      { label: 'ホーム画面へ戻る', handler: () => router.push({ path: '/dashboard' }).catch((err) => {}) },
      { label: 'ユーザー情報', handler: () => router.push({ path: '/profile' }) },
      { label: 'サポート（QA/お問い合わせ）', handler: (): any => window.open('/contact', '_blank') },
      { label: 'PolariStationについて', handler: (): any => window.open('https://polaristation.com/', '_blank') }
    ])
  },

  // ログインユーザ情報取得
  async getSelfInfo({ commit }: any) {
    const res = await getLoginUser()
    const user = res.data
    commit('SET_USER', user)
    return user
  },
  async resetPassword({ commit }: any, { email }: store.actions.ResetPasswordParam) {
    const res = await confirmEmail(email)
    return res.data
  },
  async confirmRegister({ commit }: any, token: string) {
    const res = await confirmRegister(token)
    return res.data
  },

  async register(
    { commit }: any,
    {
      nickName,
      birth,
      email,
      password,
      confirmPassword,
      answerChoice,
      answerNote,
      agreementCheck,
      personalAgreementCheck
    }: store.actions.RegisterParam
  ) {
    try {
      if (!agreementCheck || !personalAgreementCheck) {
        Vue.notify({
          text: '入力内容に誤りがあります。',
          type: 'error'
        })
        return
      }
      const { message } = await register(
        nickName,
        birth,
        email,
        password,
        confirmPassword,
        answerChoice,
        answerNote
      )
      Vue.notify({
        text: message,
        type: 'success'
      })
      return router.push({ path: '/register/temporary/complete' })
    } catch ({ response }) {
      return response.data
    }
  },

  async updateProfile(
    { commit, state }: any,
    {
      nickName,
      birthDate,
      email,
      image,
      password,
      confirmPassword,
      destroyImage
    }: store.actions.UpdateProfileParam
  ) {
    try {
      const user = state.user || {}
      const { data, message } = await updateProfile(
        nickName,
        birthDate,
        email,
        image,
        password,
        confirmPassword,
        destroyImage
      )
      Vue.notify({
        text: message,
        type: 'success'
      })

      commit('SET_USER', { ...user, ...data })
      return data
    } catch ({ response }) {
      let message = !response.data.errors.length ? response.data.message : response.data.errors.join('<br/>')
      Vue.notify({
        text: message,
        type: 'error'
      })
      return response.data
    }
  },
  async withdraw({ commit }: any) {
    const res = await withdraw()
    localStorage.removeItem('token')
    return res.data
  },

  async verifyToken({ commit }: any, { token, type }: store.actions.VerifyParam) {
    const res = await verifyToken(token, type)
    return res.data
  },

  async newPassword({ commit }: any, { token, new_password, confirm_password }: store.actions.NewPasswordParams) {
    const res = await newPassword(token, new_password, confirm_password)
    return res.data
  },

  async updateBookmarkStatus({ commit }: any, { id, bookmark }: any) {
    try {
      const { data } = await updateBookmarksStatus(id, bookmark)
      let message = bookmark ? 'Myコンテンツに登録しました' : 'Myコンテンツを解除しました'
      Vue.notify({
        text: message,
        type: 'success'
      })
      return data
    } catch ({ response }) {
      Vue.notify({
        text: response.data.errors.join('<br/>'),
        type: 'error'
      })
    }
  },

  async contact({ commit }: any, { email, description }: any) {
    try {
      const { message } = await contact(email, description)
      Vue.notify({
        text: message,
        type: 'success'
      })
      commit('SET_CONTACT_IS_COMPLETE', true)
      return router.push({ path: '/contact/complete' })
    } catch ({ response }) {
      Vue.notify({
        text: response.data.errors.join('<br/>'),
        type: 'error'
      })
    }
  },

  async suspend({ commit }: any, answers: any) {
    commit('SET_ANWSER', answers)
  }
}
const mutations = {
  SET_TITLE(state: store.AppState, title: string) {
    Vue.set(state, 'title', title)
    document.title = `${process.env.VUE_APP_NAME} - ${title}`
  },

  SET_MENU(state: store.AppState, links: store.MenuLink[]) {
    Vue.set(state, 'menu', links)
  },

  SET_LOADING(state: store.AppState, loading: boolean) {
    Vue.set(state, 'loading', loading)
  },

  SET_MESSAGE(state: store.AppState, param: store.actions.MessageParam) {
    Vue.set(state, 'message', Object.assign({}, state.message, param))
  },

  SET_TOKEN(state: store.AppState, token: string) {
    Vue.set(state, 'token', token)
    setToken(token)
  },

  SET_USER(state: store.AppState, user: model.User) {
    Vue.set(state, 'user', user)
  },
  SET_CONTACT_IS_COMPLETE(state: store.AppState, complete: boolean) {
    state.contact.isComplete = complete
  },
  SET_ANWSER(state: store.AppState, answers: any) {
    state.answers.push(answers)
  }
}

export default new Vuex.Store<store.AppState>({
  state,
  actions,
  mutations
})
